import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, Modal, ShareMenu, StatusBadge, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Menu } from '@mantine/core';
import {
  MoreVertical, Pencil, RefreshCw, Share2, Trash2, CheckCircle2, PackageCheck, XCircle,
} from 'lucide-react';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { PurchaseOrder } from '@kss/api';
import { purchaseOrderService, enumLabel } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';
import { encodeId } from '../../utils/idCodec';
import { NameCell } from '../../hooks/useNameMaps';

/* SSO ISSUE 2b (2026-07-16) — Purchase-order lifecycle enum (mirrors
   `PurchaseOrderStatusLabels` in enums.ts, which was aligned to the
   backend OrderStatus enum this pass). The status-action menu below
   only offers forward transitions: Draft → Confirmed → Delivered
   (skipping Shipped is intentional for POs — the SPA doesn't track
   in-transit for supplier deliveries yet). Cancel is offered from
   every non-terminal state.

   "Mark Delivered" is the trigger that increments Stock.Quantity +
   emits a StockMovement.In audit row — see PurchaseOrderService
   .UpdateAsync (backend). Previously the receipt fired on Create,
   which meant every draft PO immediately inflated on-hand. */
const STATUS_DRAFT = 1;
const STATUS_CONFIRMED = 2;
const STATUS_DELIVERED = 4;
const STATUS_CANCELLED = 5;

export default function AllPurchaseOrders() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const perms = useModulePerms('PurchaseOrder');
  const [rows, setRows] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<PurchaseOrder | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Cancel needs a confirm dialog; the forward moves (Confirm / Mark
  // Delivered) fire directly from the menu. `statusBusyId` blocks
  // double-clicks that would resurrect a stale status.
  const [confirmCancel, setConfirmCancel] = useState<PurchaseOrder | null>(null);
  /* Suppliers have no storefront account and there is no public PO page, so
     this shares the ORDER SUMMARY as text — the buyer attaches the PDF from
     the existing Download action. Deliberately no URL: a panel link would
     land the supplier on the admin login screen. */
  const [shareRow, setShareRow] = useState<PurchaseOrder | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await purchaseOrderService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load purchase orders';
      setError(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.purchaseOrderId) return;
    setDeleting(true);
    try {
      await purchaseOrderService.delete(confirmDelete.purchaseOrderId);
      toast.success('Deleted', `PO removed`);
      setConfirmDelete(null); await load();
    } catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  /**
   * Advance / cancel a PO via PUT. Spreading the row round-trips every
   * scalar column so the strict-mode binder doesn't `UNKNOWN_FIELD` on
   * a partial payload. Note: `/api/PurchaseOrder/all` returns headers
   * WITHOUT items[] — that's fine for the flip itself, but it means
   * PurchaseOrderService has to fall back to a per-PO item fetch when
   * applying stock on the Delivered transition (see the backend
   * `_itemRepo` fallback in ApplyStockInForDeliveryAsync).
   */
  const changeStatus = async (row: PurchaseOrder, next: number) => {
    if (!row.purchaseOrderId) return;
    setStatusBusyId(row.purchaseOrderId);
    try {
      const payload: PurchaseOrder = { ...row, status: next };
      await purchaseOrderService.update([payload]);
      toast.success('PO updated', `${row.orderNumber ?? ''} → ${enumLabel('PurchaseOrderStatus', next)}`);
      await load();
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setStatusBusyId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!confirmCancel) return;
    const row = confirmCancel;
    setConfirmCancel(null);
    await changeStatus(row, STATUS_CANCELLED);
  };

  const columns: DataGridColumn<PurchaseOrder>[] = [
    { accessor: 'orderNumber', title: 'PO #', sortable: true, filterable: true, width: 140 },
    { accessor: 'orderDate', title: 'Date', sortable: true, render: (r) => r.orderDate ? new Date(r.orderDate).toLocaleDateString() : '—' },
    // /api/Supplier/all is now live (2026-05-02). Resolve via NameCell.
    { accessor: 'supplierId', title: 'Supplier', render: (r) => <NameCell kind="supplier" id={r.supplierId} /> },
    { accessor: 'totalAmount', title: 'Total (₹)', sortable: true, textAlign: 'right', render: (r) => Number(r.totalAmount ?? 0).toLocaleString('en-IN') },
    { accessor: 'status', title: 'Status', sortable: true,
      render: (r) => <StatusBadge status={enumLabel('PurchaseOrderStatus', toEnumValue(r.status))} /> },
    { accessor: 'purchaseOrderId', title: 'Actions', width: 150,
      render: (r) => {
        const statusInt = toEnumValue(r.status) ?? 0;
        const busy = !!r.purchaseOrderId && statusBusyId === r.purchaseOrderId;
        /* Forward-only. Skipping Shipped for POs — the SPA doesn't
           track in-transit for supplier receipts yet, so Confirm →
           Delivered is the useful two-step. Cancel is offered from
           every non-terminal state. */
        const canConfirm = perms.canUpdate && statusInt === STATUS_DRAFT;
        const canDeliver =
          perms.canUpdate && (statusInt === STATUS_CONFIRMED || statusInt === STATUS_DRAFT);
        const canCancel =
          perms.canUpdate &&
          statusInt > 0 &&
          statusInt !== STATUS_DELIVERED &&
          statusInt !== STATUS_CANCELLED;
        const hasStatusAction = canConfirm || canDeliver || canCancel;
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); setShareRow(r); }}
              title="Share with supplier"
            >
              <Share2 className="h-3.5 w-3.5" />
            </button>
            {hasStatusAction && (
              <Menu shadow="md" width={180} position="bottom-end" withinPortal>
                <Menu.Target>
                  <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover disabled:opacity-50"
                    onClick={(e) => e.stopPropagation()}
                    title="Status actions"
                  >
                    <MoreVertical className="h-3.5 w-3.5" />
                  </button>
                </Menu.Target>
                <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                  {canConfirm && (
                    <Menu.Item leftSection={<CheckCircle2 className="h-3.5 w-3.5" />} onClick={() => changeStatus(r, STATUS_CONFIRMED)}>
                      Confirm
                    </Menu.Item>
                  )}
                  {canDeliver && (
                    <Menu.Item leftSection={<PackageCheck className="h-3.5 w-3.5" />} onClick={() => changeStatus(r, STATUS_DELIVERED)}>
                      Mark Delivered
                    </Menu.Item>
                  )}
                  {canCancel && (
                    <Menu.Item
                      color="red"
                      leftSection={<XCircle className="h-3.5 w-3.5" />}
                      onClick={() => setConfirmCancel(r)}
                    >
                      Cancel PO
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
            {perms.canUpdate && (
              <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
                onClick={(e) => { e.stopPropagation(); if (r.purchaseOrderId) navigate(`/panel/purchase-orders/${encodeId(r.purchaseOrderId)}`); }} title="Edit">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {perms.canDelete && (
              <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      } },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<PurchaseOrder>
        title={<span className="hidden sm:inline">All purchase orders</span>}
        columnPicker data={rows} columns={columns}
        selectable selectedIds={selected} onSelectedChange={setSelected}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        // Newest PO on top — orderNumber embeds a date prefix that sorts
        // WRONG chronologically when ascending. See ORDER_WORKFLOW_AUDIT
        // Issue 6.
        initialSortStatus={{ columnAccessor: 'orderDate', direction: 'desc' }}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {/* StandardAddDropdown internally gates every menu item by
                useModulePerms('PurchaseOrder') and returns null when the
                user has no create/import/export perms — no outer gate
                needed. Passing `module` explicitly since the URL slug
                is "purchase-orders" but the backend module is
                "PurchaseOrder". */}
            <StandardAddDropdown
              basePath="/panel/purchase-orders"
              newLabel="New PO"
              service={purchaseOrderService}
              module="PurchaseOrder"
              templateFields={['orderNumber', 'orderDate', 'status', 'totalAmount', 'supplierId', 'notes']}
            />
          </div>
        }
      />
      {/* Supplier share — message only. See the note on `shareRow`. */}
      <Modal
        open={!!shareRow}
        onClose={() => setShareRow(null)}
        title={shareRow ? `Share PO ${shareRow.orderNumber ?? ''}` : ''}
        width={520}
      >
        {shareRow && (() => {
          const message = [
            `*Purchase Order ${shareRow.orderNumber ?? ''}*`,
            shareRow.orderDate ? `Ordered: ${String(shareRow.orderDate).slice(0, 10)}` : null,
            shareRow.expectedDate ? `Expected: ${String(shareRow.expectedDate).slice(0, 10)}` : null,
            `Total: ₹${Number(shareRow.totalAmount ?? 0).toLocaleString('en-IN')}`,
          ].filter(Boolean).join('\n');
          return (
            <div className="grid gap-3">
              <Alert variant="info">
                Suppliers don't have a storefront login, so this sends the order summary
                as a message with no link. Use <strong>Download PDF</strong> on the order
                first if you want to attach the full document.
              </Alert>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-content-secondary">
                  Message
                </span>
                <textarea
                  readOnly
                  rows={4}
                  value={message}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded-md border border-border bg-surface-card-hover px-2 py-2 text-xs text-content-primary"
                />
              </label>
              <div>
                <ShareMenu
                  content={{
                    title: `Purchase Order ${shareRow.orderNumber ?? ''}`,
                    message,
                    // No link — composeShareText appends this, and an empty
                    // string keeps the message clean rather than trailing a
                    // dead URL.
                    url: '',
                  }}
                  label="Send via…"
                  onResult={(_c, result) => {
                    if (result === 'copied') toastRef.current.success('Copied');
                    if (result === 'unsupported') toastRef.current.error('Sharing unavailable');
                  }}
                />
              </div>
            </div>
          );
        })()}
      </Modal>

      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete} title="Delete purchase order"
        message={confirmDelete ? `Remove PO ${confirmDelete.orderNumber || ''}? This cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
      <ConfirmDialog isOpen={!!confirmCancel} onClose={() => setConfirmCancel(null)}
        onConfirm={handleConfirmCancel} title="Cancel Purchase Order?"
        message={confirmCancel ? `Cancel PO ${confirmCancel.orderNumber || ''}? Stock will NOT be received.` : ''}
        confirmLabel="Cancel PO" cancelLabel="Keep PO" variant="danger" />
    </div>
  );
}

/**
 * Coerce a PO enum field (`status`) to a real integer for `enumLabel`.
 * Backend ships ints, but legacy rows / mocks may carry strings — and
 * `Number(undefined)` is NaN, which slips past `enumLabel`'s null guard
 * and renders "Purchase Order Status #NaN" on screen. Normalise both.
 */
function toEnumValue(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}
